import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, KeyRound, ShieldCheck, Mail, XCircle } from "lucide-react";
import { toast } from "sonner";

import SEO from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, unwrap, setCleanerToken } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

// A3 — magic-link password setup. Reached from the "Create my password" button
// in the cleaner_password_setup email sent right after an admin approves an
// application. No temp password is ever shown — the cleaner picks their own
// from the very first step, then gets auto-logged-in straight to the dashboard.
export default function CleanerSetupPasswordPage() {
  const { locale } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const L = (en, es) => (locale === "es" ? es : en);

  const [checking, setChecking] = useState(true);
  const [errorCode, setErrorCode] = useState("");
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({ next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const verify = useCallback(async () => {
    if (!token) { setErrorCode("token_invalid"); setChecking(false); return; }
    try {
      const r = await api.get(`/cleaner/setup-password/verify?token=${encodeURIComponent(token)}`);
      setUser(unwrap(r)?.user || null);
    } catch (err) {
      setErrorCode(err?.response?.data?.detail?.code || "token_invalid");
    } finally {
      setChecking(false);
    }
  }, [token]);

  useEffect(() => { verify(); }, [verify]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) {
      toast.error(L("New password must be at least 8 characters.", "La nueva contraseña debe tener al menos 8 caracteres."));
      return;
    }
    if (!/[A-Za-z]/.test(form.next) || !/\d/.test(form.next)) {
      toast.error(L("New password must include a letter and a number.", "La nueva contraseña debe incluir una letra y un número."));
      return;
    }
    if (form.next !== form.confirm) {
      toast.error(L("Passwords don't match.", "Las contraseñas no coinciden."));
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/cleaner/setup-password", { token, password: form.next });
      const data = unwrap(r);
      if (data?.access_token) setCleanerToken(data.access_token);
      toast.success(L("Password set — welcome aboard!", "Contraseña configurada — ¡bienvenido/a!"));
      navigate("/cleaner/dashboard", { replace: true });
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code === "token_used" || code === "token_expired" || code === "token_invalid") {
        setErrorCode(code);
      } else {
        toast.error(err?.response?.data?.detail?.message || L("Could not set your password", "No pudimos configurar tu contraseña"));
      }
    } finally {
      setSaving(false);
    }
  };

  const ERROR_COPY = {
    token_expired: {
      title: L("This link has expired", "Este link venció"),
      body: L("Password setup links expire after 7 days for your security.", "Por seguridad, los links para crear tu contraseña vencen a los 7 días."),
    },
    token_used: {
      title: L("This link was already used", "Este link ya fue usado"),
      body: L("You've already set your password with this link. Try logging in instead.", "Ya configuraste tu contraseña con este link. Intenta ingresar directamente."),
    },
    token_invalid: {
      title: L("Invalid link", "Link inválido"),
      body: L("This password-setup link is not valid.", "Este link para crear tu contraseña no es válido."),
    },
  };

  return (
    <div className="min-h-screen bg-[#0C1A1A] grid place-items-center px-4" data-testid="cleaner-setup-password-page">
      <SEO title={L("Set your password — Ever Clean Home", "Configura tu contraseña — Ever Clean Home")} noindex />
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="w-14 h-14 rounded-xl bg-white grid place-items-center p-2"><img src="/brand/logo-everclean-icon-192.png" alt="Ever Clean Home" className="w-full h-full object-contain" /></span>
          <span className="font-display font-extrabold text-xl text-white">Ever Clean Home</span>
        </Link>

        <div className="bg-white rounded-2xl p-8 shadow-xl">
          {checking ? (
            <div className="grid place-items-center py-10" data-testid="setup-password-checking">
              <Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" />
            </div>
          ) : errorCode ? (
            <div className="text-center py-4" data-testid="setup-password-error">
              <span className="w-12 h-12 rounded-full bg-rose-100 grid place-items-center mx-auto mb-4">
                <XCircle className="w-6 h-6 text-rose-600" />
              </span>
              <h1 className="font-display text-xl font-extrabold tracking-tighter" data-testid="setup-password-error-title">
                {(ERROR_COPY[errorCode] || ERROR_COPY.token_invalid).title}
              </h1>
              <p className="text-sm text-[#4B5563] mt-2">{(ERROR_COPY[errorCode] || ERROR_COPY.token_invalid).body}</p>
              <div className="mt-6 flex flex-col gap-2">
                {errorCode === "token_used" && (
                  <Link to="/cleaner/login">
                    <Button className="w-full h-11 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-semibold" data-testid="setup-password-go-login">
                      {L("Go to login", "Ir a ingresar")}
                    </Button>
                  </Link>
                )}
                <a href="mailto:contact@evercleanhome.online">
                  <Button variant="outline" className="w-full rounded-xl" data-testid="setup-password-contact-support">
                    <Mail className="w-4 h-4 mr-2" /> {L("Contact support", "Contactar soporte")}
                  </Button>
                </a>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <KeyRound className="w-5 h-5 text-[#0EA5A4]" />
                <h1 className="font-display text-2xl font-extrabold tracking-tighter" data-testid="setup-password-title">
                  {L(`Welcome${user?.first_name ? `, ${user.first_name}` : ""}!`, `¡Bienvenido/a${user?.first_name ? `, ${user.first_name}` : ""}!`)}
                </h1>
              </div>
              <p className="text-sm text-[#4B5563] mt-1">{L("Please create your password to activate your account.", "Crea tu contraseña para activar tu cuenta.")}</p>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label className="text-xs">{L("Email", "Email")}</Label>
                  <Input value={user?.email || ""} readOnly disabled className="rounded-xl mt-1 bg-[#F5F5F4]" data-testid="setup-password-email" />
                </div>
                <div>
                  <Label className="text-xs">{L("New password", "Nueva contraseña")}</Label>
                  <Input type="password" value={form.next} onChange={(e) => set({ next: e.target.value })} className="rounded-xl mt-1" required data-testid="setup-password-new" />
                  <p className="text-[11px] text-[#9CA3AF] mt-1">{L("At least 8 characters, with a letter and a number.", "Al menos 8 caracteres, con una letra y un número.")}</p>
                </div>
                <div>
                  <Label className="text-xs">{L("Confirm password", "Confirmar contraseña")}</Label>
                  <Input type="password" value={form.confirm} onChange={(e) => set({ confirm: e.target.value })} className="rounded-xl mt-1" required data-testid="setup-password-confirm" />
                </div>
                <Button type="submit" disabled={saving} className="w-full h-11 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-semibold" data-testid="setup-password-submit">
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-2" /> {L("Set my password", "Configurar mi contraseña")}</>}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
